import { BUILD_ID } from 'utils/app-version';
import { maskPersonalIdentityNumbers } from 'utils/mask-personal-identity-numbers';
import { getLastBoundaryEventId } from './error-reporting';

/**
 * DD-3780: what support and the help assistant may know about a red-screen crash. Never
 * the component's props, state or request data (they can hold patient data): the error
 * message only, masked, with quoted values dropped and the length capped.
 */
export type CrashSummary = {
    message: string;
    page: string;
    at: string;
    buildId?: string;
    errorReportId?: string;
};

const STORAGE_KEY = 'denthub.lastCrash';
const MAX_MESSAGE_LENGTH = 300;
/** After this long the crash is not what the user means by "it just crashed". */
const MAX_AGE_MS = 10 * 60 * 1000;

const cleanMessage = (message: unknown): string =>
    maskPersonalIdentityNumbers(String(message ?? 'Unknown error').replace(/"[^"]*"|'[^']*'/g, '"…"'))
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_MESSAGE_LENGTH);

export const buildCrashSummary = (error: unknown, now: Date = new Date()): CrashSummary => ({
    message: cleanMessage((error as Error)?.message ?? error),
    page: `${window.location.pathname}${window.location.hash}`,
    at: now.toISOString(),
    buildId: BUILD_ID || undefined,
    errorReportId: getLastBoundaryEventId(),
});

/** Kept for this tab only (the red screen clears localStorage), so the next load can offer help. */
export const rememberCrash = (summary: CrashSummary): void => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(summary));
    } catch {
        // Storage unavailable: the offer after the reload is a convenience, not required.
    }
};

/** The crash from just before this load, once; nothing if it is older than ten minutes. */
export const takeRememberedCrash = (now: Date = new Date()): CrashSummary | undefined => {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
        const summary = stored ? (JSON.parse(stored) as CrashSummary) : undefined;
        return summary && now.getTime() - new Date(summary.at).getTime() <= MAX_AGE_MS ? summary : undefined;
    } catch {
        return undefined;
    }
};

/** The diagnostics block appended to a support ticket. */
export const crashDetailsForSupport = (summary: CrashSummary): string =>
    [
        '--- Crash details ---',
        `Error: ${summary.message}`,
        `Page: ${summary.page}`,
        `Time: ${summary.at}`,
        summary.buildId ? `Build: ${summary.buildId}` : undefined,
        summary.errorReportId ? `Error report (GlitchTip): ${summary.errorReportId}` : undefined,
    ]
        .filter(Boolean)
        .join('\n');
